/**
 * London Smoke & Cure — Njord sensor alert desktop / in-app notification helpers.
 * Uses the same alert feed as Njord Overview (`buildAlertFeed`).
 */

/**
 * Stable key for deduping ongoing threshold alerts.
 * @param {{ sensorId?: string, severity?: string, metric?: string }} alert
 */
export function njordAlertNotificationKey(alert) {
    const id = String(alert?.sensorId || alert?.id || '').trim();
    const severity = String(alert?.severity || 'warning').trim();
    const metric = String(alert?.metric || 'unknown').trim();
    return `${id}:${severity}:${metric}`;
}

/**
 * @param {ReturnType<import('./njordMonitorUtils').buildAlertFeed>} alerts
 * @returns {Set<string>}
 */
export function njordAlertKeysSet(alerts) {
    return new Set((alerts || []).map((a) => njordAlertNotificationKey(a)));
}

/**
 * Alerts that were not present in the previous key set (new or escalated).
 * @param {Set<string>|null|undefined} prevKeys
 * @param {ReturnType<import('./njordMonitorUtils').buildAlertFeed>} alerts
 */
export function findNewNjordSensorAlerts(prevKeys, alerts) {
    if (!(prevKeys instanceof Set)) return [];
    return (alerts || []).filter((a) => !prevKeys.has(njordAlertNotificationKey(a)));
}

/**
 * @param {{ sensorName?: string, zone?: string, severity?: string, message?: string, value?: string, threshold?: string }} alert
 */
export function formatNjordSensorAlertNotification(alert) {
    const name = String(alert?.sensorName || 'Sensor').trim();
    const zone = String(alert?.zone || 'Facility').trim();
    const message = String(alert?.message || 'Threshold attention').trim();
    const value = String(alert?.value || '').trim();
    const threshold = String(alert?.threshold || '').trim();
    const title =
        alert?.severity === 'critical' ? `Njord critical: ${name}` : `Njord alert: ${name}`;
    const parts = [zone, message];
    if (value) parts.push(value);
    if (threshold) parts.push(threshold);
    return { title, body: parts.join(' · ') };
}

/**
 * Odin deep link to Njord sensor detail.
 * @param {string} sensorId
 */
export function njordSensorAlertNavigateHref(sensorId) {
    const id = encodeURIComponent(String(sensorId || '').trim());
    return `/odin#tab=njord&sid=${id}`;
}
